import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState, type ReactNode } from 'react';
import { cn, prefersReducedMotion } from '../../lib/utils';

export type ToastVariant = 'success' | 'warning' | 'error' | 'info';

export interface ToastProps {
  id?: string;
  variant?: ToastVariant;
  title: ReactNode;
  description?: ReactNode;
  /** Auto-dismiss after N ms. 0 = no auto-dismiss. Default: 5000 */
  duration?: number;
  onDismiss?: () => void;
}

const dotStyles: Record<ToastVariant, string> = {
  success: 'bg-success-400',
  warning: 'bg-warning-400',
  error: 'bg-error-400',
  info: 'bg-info-400',
};

/**
 * Single toast element. Use via a Toast.Provider + ToastViewport in production,
 * or render directly for simple usage.
 */
export function Toast({
  variant = 'success',
  title,
  description,
  duration = 5000,
  onDismiss,
}: ToastProps) {
  const [show, setShow] = useState(true);
  const reduced = prefersReducedMotion();

  useEffect(() => {
    if (duration <= 0) return;
    const t = setTimeout(() => setShow(false), duration);
    return () => clearTimeout(t);
  }, [duration]);

  return (
    <AnimatePresence onExitComplete={onDismiss}>
      {show && (
        <motion.div
          role="status"
          aria-live="polite"
          initial={reduced ? false : { opacity: 0, y: 12 }}
          animate={reduced ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, y: 12 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            'flex items-start gap-3 rounded-md p-3',
            'shadow-elev-3 border border-border-subtle bg-bg-elevated',
            'min-w-[240px] max-w-sm text-sm text-text-primary'
          )}
        >
          <span
            className={cn('mt-1.5 h-2 w-2 flex-shrink-0 rounded-full', dotStyles[variant])}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <strong className="font-medium">{title}</strong>
            {description && <p className="mt-0.5 text-text-secondary">{description}</p>}
          </div>
          <button
            onClick={() => setShow(false)}
            aria-label="Dismiss"
            className="-mt-1 p-0.5 text-lg leading-none text-text-tertiary hover:text-text-primary"
          >
            ×
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
